import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { createProduct } from '../services/productService';
import { getCategories } from '../services/categoryService';
import { getSubcategories } from '../services/subcategoryService';
import { getTags, createTag } from '../services/tagService';
import { useAuth } from '../context/AuthContext';

const ProductBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [validationWarnings, setValidationWarnings] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const { token } = useAuth();
    
    // Upload progress tracking
    const [uploadProgress, setUploadProgress] = useState({
        current: 0,
        total: 0,
        currentProductName: '',
        status: 'idle' // 'idle' | 'parsing' | 'uploading' | 'complete'
    });

    const generateUniversalTemplate = () => {
        // Match your n8n Google Sheets template structure exactly
        const baseHeaders = [
            'name', 'brand', 'description', 'shortDescription', 'imageUrl', 'affiliateLink',
            'price', 'discount', 'rating', 'reviewCount', 'isActive', 'isFeatured',
            'categoryName', 'subcategoryName', 'highlights', 'pros', 'cons',
            'keyIngredients', 'benefits', 'suitability', 'fragrance', 'size', 'badges',
            'tags', 'radianceTake', 'currency'
        ];

        // Add n8n-specific fields
        const n8nFields = [
            'unique_key', 'rowNumber'
        ];

        return [...baseHeaders, ...n8nFields].join('\t');
    };

    const downloadTemplate = () => {
        const csvContent = generateUniversalTemplate();
        const blob = new Blob([csvContent], { type: 'text/tab-separated-values' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'n8n-product-template.tsv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && 
                selectedFile.type !== 'text/tab-separated-values' && 
                !selectedFile.name.endsWith('.csv') && 
                !selectedFile.name.endsWith('.tsv')) {
                toast.error('Please select a CSV or TSV file');
                return;
            }
            // Reset validation state when new file is selected
            setValidationErrors([]);
            setValidationWarnings([]);
            setShowErrors(false);
            setFile(selectedFile);
            parseCSV(selectedFile);
        }
    };

    // Proper CSV/TSV parser that handles quoted fields with commas and tabs
    const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        // Detect if it's tab-separated or comma-separated
        const hasTabs = line.includes('\t');
        const separator = hasTabs ? '\t' : ',';
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Double quote escape
                    current += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === separator && !inQuotes) {
                // End of field
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        
        // Add final field
        result.push(current.trim());
        return result;
    };

    // Enhanced CSV parser that handles multi-line content within quotes
    const parseCSVContent = (text) => {
        const rows = [];
        let currentRow = '';
        let inQuotes = false;
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const nextChar = text[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentRow += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                    currentRow += char;
                }
            } else if (char === '\n' && !inQuotes) {
                // End of row (not inside quotes)
                if (currentRow.trim()) {
                    rows.push(currentRow.trim());
                }
                currentRow = '';
            } else {
                currentRow += char;
            }
        }
        
        // Add final row
        if (currentRow.trim()) {
            rows.push(currentRow.trim());
        }
        
        return rows;
    };

    // Comprehensive row validation function
    const validateRow = (row, rowNumber) => {
        const errors = [];
        const warnings = [];
        
        // Required field validation
        const requiredFields = {
            'name': row.name || row.Name,
            'brand': row.brand || row.Brand,
            'categoryName': row.categoryName || row['Category Name'] || row.category_name || row.category
        };
        
        Object.entries(requiredFields).forEach(([field, value]) => {
            if (!value || value.trim() === '') {
                errors.push({
                    row: rowNumber,
                    field: field,
                    error: `${field} is required but is empty`,
                    value: value || 'empty'
                });
            }
        });
        
        // Price validation
        const price = row.price || row.Price;
        if (price && price.trim() !== '') {
            const priceNum = parseFloat(price);
            if (isNaN(priceNum)) {
                errors.push({
                    row: rowNumber,
                    field: 'price',
                    error: 'Price must be a valid number',
                    value: price
                });
            } else if (priceNum < 0) {
                errors.push({
                    row: rowNumber,
                    field: 'price',
                    error: 'Price cannot be negative',
                    value: price
                });
            } else if (priceNum > 10000) {
                warnings.push({
                    row: rowNumber,
                    field: 'price',
                    warning: 'Price seems very high, please verify',
                    value: price
                });
            }
        }
        
        // Rating validation
        const rating = row.rating || row.Rating;
        if (rating && rating.trim() !== '') {
            const ratingNum = parseFloat(rating);
            if (isNaN(ratingNum)) {
                errors.push({
                    row: rowNumber,
                    field: 'rating',
                    error: 'Rating must be a valid number',
                    value: rating
                });
            } else if (ratingNum < 0 || ratingNum > 5) {
                errors.push({
                    row: rowNumber,
                    field: 'rating',
                    error: 'Rating must be between 0 and 5',
                    value: rating
                });
            }
        }
        
        // Discount validation
        const discount = row.discount || row.Discount;
        if (discount && discount.trim() !== '') {
            const discountNum = parseFloat(discount);
            if (isNaN(discountNum)) {
                errors.push({
                    row: rowNumber,
                    field: 'discount',
                    error: 'Discount must be a valid number',
                    value: discount
                });
            } else if (discountNum < 0 || discountNum > 100) {
                errors.push({
                    row: rowNumber,
                    field: 'discount',
                    error: 'Discount must be between 0 and 100',
                    value: discount
                });
            }
        }
        
        // URL validation
        const imageUrl = row.imageUrl || row['Image URL'] || row.image_url || row.image;
        if (imageUrl && imageUrl.trim() !== '') {
            try {
                new URL(imageUrl);
            } catch {
                if (!imageUrl.startsWith('/')) {
                    errors.push({
                        row: rowNumber,
                        field: 'imageUrl',
                        error: 'Image URL must be a valid URL or start with /',
                        value: imageUrl
                    });
                }
            }
        }
        
        const affiliateLink = row.affiliateLink || row['Affiliate Link'] || row.affiliate_link;
        if (affiliateLink && affiliateLink.trim() !== '') {
            try {
                new URL(affiliateLink);
            } catch {
                errors.push({
                    row: rowNumber,
                    field: 'affiliateLink',
                    error: 'Affiliate Link must be a valid URL',
                    value: affiliateLink
                });
            }
        }
        
        // Badge validation
        const badges = row.badges || row.Badges;
        if (badges && badges.trim() !== '') {
            const validBadges = ['derm_approved', 'editors_choice', 'best_seller', 'trending'];
            const badgeList = badges.split(';').map(b => b.trim().toLowerCase().replace(/[^a-z]/g, '_'));
            const invalidBadges = badgeList.filter(badge => !validBadges.includes(badge) && badge !== '');
            if (invalidBadges.length > 0) {
                errors.push({
                    row: rowNumber,
                    field: 'badges',
                    error: `Invalid badges: ${invalidBadges.join(', ')}. Valid options: ${validBadges.join(', ')}`,
                    value: badges
                });
            }
        }
        
        // Boolean field validation
        const booleanFields = {
            'isActive': row.isActive || row['Is Active'] || row.is_active,
            'isFeatured': row.isFeatured || row['Is Featured'] || row.is_featured
        };
        
        Object.entries(booleanFields).forEach(([field, value]) => {
            if (value && value.trim() !== '') {
                const validBooleans = ['true', 'false', 'yes', 'no', '1', '0', 'active', 'inactive'];
                if (!validBooleans.includes(value.toLowerCase())) {
                    errors.push({
                        row: rowNumber,
                        field: field,
                        error: `${field} must be one of: ${validBooleans.join(', ')}`,
                        value: value
                    });
                }
            }
        });
        
        // Key Ingredients validation
        const keyIngredients = row.keyIngredients || row['Key Ingredients'] || row.key_ingredients;
        if (keyIngredients && keyIngredients.trim() !== '') {
            // Support both bullet points (•) and semicolons (;) as separators
            const separator = keyIngredients.includes('•') ? '•' : ';';
            const ingredients = keyIngredients.split(separator).map(item => item.trim()).filter(item => item !== '');
            ingredients.forEach((ingredient, index) => {
                // Extract name and benefit from various formats
                let name = '';
                let benefit = '';
                
                if (ingredient.includes('→:')) {
                    // Handle "Name →: Benefit" format
                    const parts = ingredient.split('→:');
                    name = parts[0].trim();
                    benefit = parts.slice(1).join('→:').trim();
                } else if (ingredient.includes('→')) {
                    // Handle "Name → Benefit" format
                    const parts = ingredient.split('→');
                    name = parts[0].trim();
                    benefit = parts.slice(1).join('→').trim();
                } else if (ingredient.includes(':')) {
                    // Handle "Name: Benefit" format
                    const parts = ingredient.split(':');
                    name = parts[0].trim();
                    benefit = parts.slice(1).join(':').trim();
                } else {
                    // No separator, treat as name only
                    name = ingredient.trim();
                }
                
                // Clean up any trailing arrows or colons from the name
                name = name.replace(/\s*[→:]\s*$/, '').trim();
                
                // Validate name and benefit
                if ((ingredient.includes('→') || ingredient.includes(':')) && !name) {
                    errors.push({
                        row: rowNumber,
                        field: 'keyIngredients',
                        error: `Ingredient ${index + 1}: Name cannot be empty when using name→benefit or name:benefit format`,
                        value: ingredient
                    });
                }
                if (name && name.length > 100) {
                    errors.push({
                        row: rowNumber,
                        field: 'keyIngredients',
                        error: `Ingredient ${index + 1}: Name too long (max 100 characters)`,
                        value: name
                    });
                }
                if (benefit && benefit.length > 200) {
                    errors.push({
                        row: rowNumber,
                        field: 'keyIngredients',
                        error: `Ingredient ${index + 1}: Benefit too long (max 200 characters)`,
                        value: benefit
                    });
                }
                
                // For ingredients without benefit, validate the full length
                if (!benefit && ingredient.length > 100) {
                    errors.push({
                        row: rowNumber,
                        field: 'keyIngredients',
                        error: `Ingredient ${index + 1}: Name too long (max 100 characters)`,
                        value: ingredient
                    });
                }
            });
        }
        
        // Expandable array field validations
        const expandableFields = {
            'highlights': { field: row.highlights || row.Highlights, maxLength: 200 },
            'pros': { field: row.pros || row.Pros, maxLength: 200 },
            'cons': { field: row.cons || row.Cons, maxLength: 200 },
            'benefits': { field: row.benefits || row.Benefits, maxLength: 200 },
            'suitability': { field: row.suitability || row.Suitability, maxLength: 100 }
        };
        
        Object.entries(expandableFields).forEach(([fieldName, { field, maxLength }]) => {
            if (field && field.trim() !== '') {
                const items = field.split(';').map(item => item.trim()).filter(item => item !== '');
                items.forEach((item, index) => {
                    if (item.length > maxLength) {
                        errors.push({
                            row: rowNumber,
                            field: fieldName,
                            error: `Item ${index + 1}: Too long (max ${maxLength} characters)`,
                            value: item.substring(0, 50) + '...'
                        });
                    }
                });
            }
        });
        
        // Length validations
        const name = row.name || row.Name;
        if (name && name.length > 200) {
            warnings.push({
                row: rowNumber,
                field: 'name',
                warning: 'Product name is very long (over 200 characters)',
                value: `${name.length} characters`
            });
        }
        
        const description = row.description || row.Description;
        if (description && description.length > 2000) {
            warnings.push({
                row: rowNumber,
                field: 'description',
                warning: 'Description is very long (over 2000 characters)',
                value: `${description.length} characters`
            });
        }
        
        return { errors, warnings };
    };
    
    const parseCSV = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const rows = parseCSVContent(text);
            
            if (rows.length === 0) return;
            
            const headers = parseCSVLine(rows[0]);
            
                const data = [];
                const errors = [];
                const warnings = [];
                
                // Validate headers first
                const requiredHeaders = ['name', 'brand', 'categoryName'];
                const missingHeaders = requiredHeaders.filter(required => 
                    !headers.some(header => 
                        header.toLowerCase().replace(/[^a-z]/g, '') === required.toLowerCase()
                    )
                );
                
                if (missingHeaders.length > 0) {
                    errors.push({
                        row: 'Header',
                        field: 'Required Headers',
                        error: `Missing required headers: ${missingHeaders.join(', ')}`,
                        value: 'N/A'
                    });
                }
                
                // Process data rows for preview and validation
                for (let i = 1; i < rows.length && i < 6; i++) { // Preview first 5 rows
                    const values = parseCSVLine(rows[i]);
                    const row = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index] || '';
                    });
                    
                    // Validate this row
                    const rowErrors = validateRow(row, i + 1);
                    errors.push(...rowErrors.errors);
                    warnings.push(...rowErrors.warnings);
                    
                    data.push(row);
                }
                
                setPreview(data);
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setShowErrors(errors.length > 0 || warnings.length > 0);
        };
        reader.readAsText(file);
    };

    const handleUpload = async () => {
        console.log('🚀 handleUpload called');
        
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        console.log('📁 File found, starting upload...');
        setUploading(true);
        
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                console.log('📄 FileReader onload triggered');
                const text = e.target.result;
                console.log('📄 CSV text length:', text.length);
                const rows = parseCSVContent(text);
                console.log('📄 Parsed rows count:', rows.length);
                
                if (rows.length === 0) {
                    toast.error('CSV file is empty');
                    setUploading(false);
                    return;
                }
                
                const headers = parseCSVLine(rows[0]);
                
                const productsData = [];
                for (let i = 1; i < rows.length; i++) {
                    const values = parseCSVLine(rows[i]);
                    const row = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index] || '';
                    });
                    
                    // Helper function to parse array fields (semicolon or bullet-separated)
                    const parseArrayField = (value) => {
                        if (!value || value.trim() === '') return [];
                        // Support both bullet points (•) and semicolons (;) as separators
                        const separator = value.includes('•') ? '•' : ';';
                        return value.split(separator).map(item => item.trim()).filter(item => item !== '');
                    };

                    // Helper function to parse keyIngredients as objects with name and benefit
                    const parseKeyIngredients = (value) => {
                        if (!value || value.trim() === '') return [];
                        
                        // First, split by bullet points (•) or semicolons (;) to separate different ingredients
                        const separator = value.includes('•') ? '•' : ';';
                        return value.split(separator).map(item => {
                            const trimmed = item.trim();
                            if (!trimmed) return null;
                            
                            // Check for patterns like "Name →: Benefit" or "Name → Benefit" or "Name: Benefit"
                            // Remove the arrow and colon combo (→:) and split
                            let name = '';
                            let benefit = '';
                            
                            if (trimmed.includes('→:')) {
                                // Handle "Name →: Benefit" format
                                const parts = trimmed.split('→:');
                                name = parts[0].trim();
                                benefit = parts.slice(1).join('→:').trim();
                            } else if (trimmed.includes('→')) {
                                // Handle "Name → Benefit" format
                                const parts = trimmed.split('→');
                                name = parts[0].trim();
                                benefit = parts.slice(1).join('→').trim();
                            } else if (trimmed.includes(':')) {
                                // Handle "Name: Benefit" format
                                const parts = trimmed.split(':');
                                name = parts[0].trim();
                                benefit = parts.slice(1).join(':').trim();
                            } else {
                                // If no separator, treat entire text as ingredient name with empty benefit
                                name = trimmed;
                                benefit = '';
                            }
                            
                            // Clean up any trailing arrows or colons from the name
                            name = name.replace(/\s*[→:]\s*$/, '').trim();
                            
                            return { name: name || '', benefit: benefit || '' };
                        }).filter(item => item !== null);
                    };
                    
                    // Helper function to parse expandable array fields (highlights, pros, cons, benefits, suitability)
                    const parseExpandableArrayField = (value) => {
                        if (!value || value.trim() === '') return [];
                        // Support both bullet points (•) and semicolons (;) as separators
                        const separator = value.includes('•') ? '•' : ';';
                        return value.split(separator).map(item => item.trim()).filter(item => item !== '');
                    };

                    // Helper function to parse badges (map to valid enum values)
                    const parseBadges = (value) => {
                        if (!value || value.trim() === '') return [];
                        const validBadges = ['derm_approved', 'editors_choice', 'best_seller', 'trending'];
                        return value.split(';').map(item => {
                            const trimmed = item.trim().toLowerCase().replace(/[^a-z]/g, '_');
                            return validBadges.includes(trimmed) ? trimmed : null;
                        }).filter(item => item !== null);
                    };

                    // Helper function to parse boolean fields
                    const parseBoolean = (value) => {
                        if (!value) return false;
                        return ['true', 'yes', '1', 'active'].includes(value.toLowerCase());
                    };

                    // Helper function to parse number fields
                    const parseNumber = (value) => {
                        if (!value || value.trim() === '') return undefined;
                        const num = parseFloat(value);
                        return isNaN(num) ? undefined : num;
                    };

                    // Map CSV columns to product fields
                    const productItem = {
                        // Basic fields
                        name: row.name || row.Name,
                        brand: row.brand || row.Brand,
                        description: row.description || row.Description,
                        shortDescription: row.shortDescription || row['Short Description'] || row.short_description,
                        imageUrl: row.imageUrl || row['Image URL'] || row.image_url || row.image,
                        affiliateLink: row.affiliateLink || row['Affiliate Link'] || row.affiliate_link,
                        
                        // Pricing
                        price: parseNumber(row.price || row.Price),
                        discount: parseNumber(row.discount || row.Discount),
                        
                        // Ratings
                        rating: parseNumber(row.rating || row.Rating),
                        reviewCount: parseNumber(row.reviewCount || row['Review Count'] || row.review_count),
                        
                        // Status fields
                        isActive: parseBoolean(row.isActive || row['Is Active'] || row.is_active || 'true'),
                        isFeatured: parseBoolean(row.isFeatured || row['Is Featured'] || row.is_featured || 'false'),
                        
                        // Category relationships (these will need to be resolved by backend)
                        categoryName: row.categoryName || row['Category Name'] || row.category_name || row.category,
                        subcategoryName: row.subcategoryName || row['Subcategory Name'] || row.subcategory_name || row.subcategory,
                        
                        // Expandable content fields (parsed as arrays/objects)
                        highlights: parseExpandableArrayField(row.highlights || row.Highlights || ''),
                        pros: parseExpandableArrayField(row.pros || row.Pros || ''),
                        cons: parseExpandableArrayField(row.cons || row.Cons || ''),
                        keyIngredients: parseKeyIngredients(row.keyIngredients || row['Key Ingredients'] || row.key_ingredients || ''),
                        benefits: parseExpandableArrayField(row.benefits || row.Benefits || ''),
                        suitability: parseExpandableArrayField(row.suitability || row.Suitability || ''),
                        
                        // Separate fragrance and size fields (as arrays like other expandable fields)
                        fragrance: parseExpandableArrayField(row.fragrance || row.Fragrance || ''),
                        size: parseExpandableArrayField(row.size || row.Size || ''),
                        
                        // Badges with proper enum mapping
                        badges: parseBadges(row.badges || row.Badges),
                        tags: parseArrayField(row.tags || row.Tags || row.tag || row.Tag),
                        
                        // Long text fields
                        radianceTake: row.radianceTake || row['Radiance Take'] || row.radiance_take || '',
                        
                        // Additional fields
                        currency: row.currency || row.Currency || 'USD'
                    };

                    // Debug logging to see what's being parsed
                    console.log(`Product ${i} parsed:`, {
                        // Basic fields
                        name: productItem.name,
                        brand: productItem.brand,
                        description: productItem.description,
                        shortDescription: productItem.shortDescription,
                        imageUrl: productItem.imageUrl,
                        affiliateLink: productItem.affiliateLink,
                        
                        // Pricing & ratings
                        price: productItem.price,
                        discount: productItem.discount,
                        rating: productItem.rating,
                        reviewCount: productItem.reviewCount,
                        currency: productItem.currency,
                        
                        // Status
                        isActive: productItem.isActive,
                        isFeatured: productItem.isFeatured,
                        
                        // Categories
                        categoryName: productItem.categoryName,
                        subcategoryName: productItem.subcategoryName,
                        
                        // Expandable text fields
                        highlights: productItem.highlights,
                        pros: productItem.pros,
                        cons: productItem.cons,
                        keyIngredients: productItem.keyIngredients,
                        benefits: productItem.benefits,
                        suitability: productItem.suitability,
                        
                        // New separate fields
                        fragrance: productItem.fragrance,
                        size: productItem.size,
                        
                        // Arrays
                        badges: productItem.badges,
                        badgesCount: productItem.badges?.length || 0,
                        tags: productItem.tags,
                        tagsCount: productItem.tags?.length || 0,
                        
                        // Long text
                        radianceTake: productItem.radianceTake
                    });
                    
                    // Also log the raw CSV row for comparison
                    console.log(`Product ${i} raw CSV data:`, row);
                    
                    productsData.push(productItem);
                }

                // Debug logging before sending
                console.log('About to send products data:', {
                    count: productsData.length,
                    firstProduct: productsData[0],
                    sampleFields: productsData[0] ? Object.keys(productsData[0]) : [],
                    hasToken: !!token,
                    tokenPreview: token ? token.substring(0, 20) + '...' : 'No token'
                });

                // Process products individually like the existing system
                const results = {
                    totalRows: productsData.length,
                    createdCount: 0,
                    errors: []
                };

                // Initialize progress tracking
                setUploadProgress({
                    current: 0,
                    total: productsData.length,
                    currentProductName: 'Loading categories...',
                    status: 'parsing'
                });

                // Load categories, subcategories, and tags for resolution
                const categoriesResponse = await getCategories();
                const categories = categoriesResponse.data || [];

                // Update status to uploading
                setUploadProgress(prev => ({
                    ...prev,
                    currentProductName: '',
                    status: 'uploading'
                }));

                for (let i = 0; i < productsData.length; i++) {
                    const productData = productsData[i];
                    
                    // Update progress for current product
                    setUploadProgress({
                        current: i,
                        total: productsData.length,
                        currentProductName: productData.name || `Product ${i + 1}`,
                        status: 'uploading'
                    });
                    
                    try {
                        // Resolve category
                        let categoryId = null;
                        let categoryName = null;
                        if (productData.categoryName) {
                            const category = categories.find(c => 
                                c.name.toLowerCase() === productData.categoryName.toLowerCase()
                            );
                            if (category) {
                                categoryId = category._id;
                                categoryName = category.name;
                            } else {
                                results.errors.push({
                                    row: i + 1,
                                    error: `Category "${productData.categoryName}" not found`,
                                    product: productData.name
                                });
                                continue;
                            }
                        }

                        // Resolve subcategory
                        let subcategoryId = null;
                        if (productData.subcategoryName && categoryId) {
                            const subcategoriesResponse = await getSubcategories(categoryId);
                            const subcategories = subcategoriesResponse.data || [];
                            const subcategory = subcategories.find(s => 
                                s.name.toLowerCase() === productData.subcategoryName.toLowerCase()
                            );
                            if (subcategory) {
                                subcategoryId = subcategory._id;
                                console.log(`✅ Subcategory "${productData.subcategoryName}" resolved for product ${i + 1}`);
                            } else {
                                console.warn(`⚠️ Subcategory "${productData.subcategoryName}" not found for product ${i + 1}. Available subcategories:`, subcategories.map(s => s.name));
                                results.errors.push({
                                    row: i + 1,
                                    error: `Subcategory "${productData.subcategoryName}" not found. Available: ${subcategories.map(s => s.name).join(', ')}`,
                                    product: productData.name
                                });
                                continue;
                            }
                        } else if (productData.subcategoryName && !categoryId) {
                            console.warn(`⚠️ Cannot resolve subcategory for product ${i + 1} because category was not found`);
                        } else if (!productData.subcategoryName) {
                            console.warn(`⚠️ No subcategory name provided for product ${i + 1}`);
                            results.errors.push({
                                row: i + 1,
                                error: 'Subcategory name is required but was not provided',
                                product: productData.name
                            });
                            continue;
                        }

                        // Resolve tags - create them if they don't exist
                        let tagIds = [];
                        if (productData.tags && productData.tags.length > 0 && subcategoryId) {
                            console.log(`🏷️  Processing ${productData.tags.length} tags for product ${i + 1}:`, productData.tags);
                            const tagsResponse = await getTags({ subcategoryId });
                            const availableTags = tagsResponse.data || [];
                            
                            // Find existing tags
                            const existingTags = availableTags.filter(tag => 
                                productData.tags.some(tagName => 
                                    tag.name.toLowerCase() === tagName.toLowerCase()
                                )
                            );
                            
                            // Get IDs of existing tags
                            tagIds = existingTags.map(tag => tag._id);
                            
                            // Create missing tags
                            const missingTags = productData.tags.filter(tagName => 
                                !availableTags.some(tag => 
                                    tag.name.toLowerCase() === tagName.toLowerCase()
                                )
                            );
                            
                            for (const tagName of missingTags) {
                                try {
                                    const newTag = await createTag({
                                        name: tagName,
                                        subcategory: subcategoryId
                                    });
                                    tagIds.push(newTag.data._id);
                                    console.log(`✅ Created new tag: ${tagName} for subcategory`);
                                } catch (error) {
                                    console.error(`❌ Failed to create tag "${tagName}":`, error);
                                    results.errors.push({
                                        row: i + 1,
                                        error: `Failed to create tag "${tagName}": ${error.response?.data?.message || error.message}`,
                                        product: productData.name
                                    });
                                }
                            }
                            
                            console.log(`✅ Successfully resolved ${tagIds.length} tags for product ${i + 1}`);
                        } else if (productData.tags && productData.tags.length > 0 && !subcategoryId) {
                            // Warning: tags provided but no subcategory to attach them to
                            console.warn(`⚠️ Product ${i + 1} has ${productData.tags.length} tags but no subcategory. Tags CANNOT be applied without a subcategory. Tags: ${productData.tags.join(', ')}`);
                            results.errors.push({
                                row: i + 1,
                                error: `Tags provided (${productData.tags.join(', ')}) but cannot be applied without a valid subcategory`,
                                product: productData.name
                            });
                        }

                        // Create the product using the existing createProduct API
                        console.log(`About to create product ${i + 1}:`, {
                            productData,
                            categoryId,
                            subcategoryId,
                            tagIds,
                            resolvedCategory: categories.find(c => c._id === categoryId),
                            resolvedSubcategory: subcategoryId ? 'Found' : 'Not found',
                            resolvedTagsCount: tagIds.length
                        });

                        const productPayload = {
                            name: productData.name,
                            brand: productData.brand,
                            description: productData.description || '',
                            shortDescription: productData.shortDescription || '',
                            price: productData.price || 0,
                            discount: productData.discount || 0,
                            rating: productData.rating || 4,
                            reviewCount: productData.reviewCount || 0,
                            imageUrl: productData.imageUrl || '',
                            affiliateLink: productData.affiliateLink || '',
                            isActive: productData.isActive !== undefined ? productData.isActive : true,
                            isFeatured: productData.isFeatured !== undefined ? productData.isFeatured : false,
                            currency: productData.currency || 'USD',
                            
                        // Expandable content fields (already parsed as arrays/objects)
                        highlights: productData.highlights || [],
                        pros: productData.pros || [],
                        cons: productData.cons || [],
                        keyIngredients: productData.keyIngredients || [],
                        benefits: productData.benefits || [],
                        suitability: productData.suitability || [],
                        attributes: productData.attributes || [],
                        // Separate fragrance and size fields (as arrays)
                        fragrance: productData.fragrance || [],
                        size: productData.size || [],
                        badges: productData.badges || [],
                        radianceTake: productData.radianceTake || '',
                            
                        // Relationships
                        category: categoryId,
                        subcategory: subcategoryId,
                        tags: tagIds
                        };

                        console.log(`📦 Final payload for product ${i + 1}:`, productPayload);
                        console.log(`🏷️  Tags being sent:`, {
                            tagIds: tagIds,
                            tagCount: tagIds.length,
                            categoryId: categoryId,
                            subcategoryId: subcategoryId
                        });
                        
                        await createProduct(productPayload);
                        results.createdCount++;
                        
                        // Update progress after successful creation
                        setUploadProgress(prev => ({
                            ...prev,
                            current: i + 1
                        }));
                        
                        console.log(`✅ Product ${i + 1} created successfully`);

                    } catch (error) {
                        console.error(`❌ Error creating product ${i + 1}:`, {
                            error: error,
                            errorMessage: error.message,
                            responseStatus: error.response?.status,
                            responseData: error.response?.data,
                            productData: productData,
                            productName: productData.name
                        });
                        results.errors.push({
                            row: i + 1,
                            error: error.response?.data?.message || error.message,
                            product: productData.name
                        });
                    }
                }

                // Mark upload as complete
                setUploadProgress(prev => ({
                    ...prev,
                    current: productsData.length,
                    status: 'complete',
                    currentProductName: ''
                }));

                // Show results with detailed error information
                if (results.createdCount > 0) {
                    toast.success(`Successfully uploaded ${results.createdCount} of ${results.totalRows} products`);
                }
                
                if (results.errors.length > 0) {
                    // Don't close modal if there are errors, show them instead
                    setValidationErrors(results.errors.map(err => ({
                        row: err.row,
                        field: 'Upload Error',
                        error: err.error,
                        value: err.product || 'N/A'
                    })));
                    setShowErrors(true);
                    toast.error(`${results.errors.length} products failed to upload. See details below.`);
                    
                    // Only close and refresh if all products were successful
                    if (results.createdCount === 0) {
                        setUploading(false);
                        return; // Don't close modal, show errors
                    }
                }

                // Only close if no errors or partial success
                if (results.errors.length === 0 || results.createdCount > 0) {
                    setUploading(false);
                    onSuccess();
                    onClose();
                } else {
                    setUploading(false);
                }
            };
            
            // Handle FileReader errors
            reader.onerror = (error) => {
                console.error('FileReader error:', error);
                toast.error('Failed to read the file');
                setUploading(false);
            };
            
            reader.readAsText(file);
        } catch (error) {
            console.error('Upload error:', error);
            console.error('Error response:', error.response?.data);
            
            // Show specific error message from backend
            const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
            
            // Add detailed error to validation errors
            setValidationErrors([{
                row: 'System',
                field: 'Upload Process',
                error: errorMessage,
                value: 'N/A'
            }]);
            setShowErrors(true);
            
            toast.error('Upload failed: ' + errorMessage);
            setUploading(false);
        }
        // Note: Don't use finally here - setUploading(false) is handled inside onload callback
    };

    // Reset state when modal closes
    const handleClose = () => {
        setFile(null);
        setPreview([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        setShowErrors(false);
        setUploading(false);
        setUploadProgress({
            current: 0,
            total: 0,
            currentProductName: '',
            status: 'idle'
        });
        onClose();
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#f8f8f8] rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Bulk Upload Products</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
                        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                            <li>Download the universal CSV template below</li>
                            <li>Fill in your product data (name, brand, and categoryName are required)</li>
                            <li><strong>Array fields:</strong> Use semicolons (;) or bullet points (•) to separate multiple values</li>
                            <li><strong>Boolean fields:</strong> Use true/false, yes/no, or 1/0</li>
                            <li><strong>Categories:</strong> Use exact category/subcategory names from your admin panel</li>
                            <li><strong>Category-specific fields:</strong> Only fill fields relevant to your product's category (leave others empty)</li>
                            <li>Upload the completed CSV file</li>
                            <li>Review the preview and click Upload</li>
                        </ol>
                        <div className="mt-4 p-3 bg-blue-100 rounded text-xs text-blue-700">
                            <strong>All Product Fields Supported:</strong>
                            <div className="mt-2 grid grid-cols-3 gap-1">
                                <div>
                                    <strong>Basic:</strong>
                                    <ul>
                                        <li>• name (required)</li>
                                        <li>• brand (required)</li>
                                        <li>• description</li>
                                        <li>• shortDescription</li>
                                        <li>• imageUrl</li>
                                        <li>• affiliateLink</li>
                                        <li>• price</li>
                                        <li>• discount</li>
                                        <li>• rating</li>
                                        <li>• reviewCount</li>
                                        <li>• currency</li>
                                    </ul>
                                </div>
                                <div>
                                    <strong>Categories:</strong>
                                    <ul>
                                        <li>• categoryName (required)</li>
                                        <li>• subcategoryName</li>
                                        <li>• tags (array)</li>
                                        <li>• badges (array)</li>
                                    </ul>
                                    <strong>Status:</strong>
                                    <ul>
                                        <li>• isActive (boolean)</li>
                                        <li>• isFeatured (boolean)</li>
                                    </ul>
                                </div>
                                <div>
                                    <strong>Expandable Content:</strong>
                                    <ul>
                                        <li>• highlights (text)</li>
                                        <li>• pros (text)</li>
                                        <li>• cons (text)</li>
                                        <li>• keyIngredients (text)</li>
                                        <li>• benefits (text)</li>
                                        <li>• suitability (text)</li>
                                        <li>• fragrance (text)</li>
                                        <li>• size (text)</li>
                                        <li>• radianceTake (text)</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="mt-3 text-xs text-gray-600 bg-yellow-50 p-3 rounded">
                                <strong>📝 Format Guidelines:</strong><br/>
                                • <strong>Array Fields:</strong> highlights, pros, cons, benefits, suitability - separate items with semicolons (;) or bullet points (•)<br/>
                                • <strong>Key Ingredients:</strong> Multiple formats supported:<br/>
                                &nbsp;&nbsp;- Simple: "Vitamin C;Hyaluronic Acid;Retinol" (ingredient names only)<br/>
                                &nbsp;&nbsp;- Detailed with semicolon: "Vitamin C:Antioxidant protection;Hyaluronic Acid:Deep hydration"<br/>
                                &nbsp;&nbsp;- Detailed with bullet: "Vitamin C → Antioxidant protection • Hyaluronic Acid → Deep hydration"<br/>
                                &nbsp;&nbsp;- Arrow formats: "Name →: Benefit" or "Name → Benefit" or "Name: Benefit"<br/>
                                • <strong>Badges:</strong> Use valid values: "derm_approved", "editors_choice", "best_seller", "trending" (separate with ; or •)<br/>
                                • <strong>Tags:</strong> Use semicolons (;) or bullet points (•) to separate: "Tag1;Tag2;Tag3"<br/>
                                • <strong>Fragrance & Size:</strong> Single text values in separate columns<br/>
                                • <strong>Leave fields empty</strong> if you don't have data for them
                            </div>
                        </div>
                    </div>

                    {/* Template Download */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                            <h4 className="font-medium text-gray-900">n8n-Compatible Template</h4>
                            <p className="text-sm text-gray-600">Download the template that matches your n8n Google Sheets structure with all product fields</p>
                        </div>
                        <button
                            onClick={downloadTemplate}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Download Template
                        </button>
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select CSV/TSV File
                        </label>
                        <input
                            type="file"
                            accept=".csv,.tsv"
                            onChange={handleFileChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Validation Errors and Warnings */}
                    {showErrors && (validationErrors.length > 0 || validationWarnings.length > 0) && (
                        <div className="mb-6">
                            {validationErrors.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <h4 className="font-semibold text-red-800">Errors Found ({validationErrors.length})</h4>
                                        <button
                                            onClick={() => setShowErrors(false)}
                                            className="ml-auto text-red-600 hover:text-red-800"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-red-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-red-800">Row</th>
                                                    <th className="px-3 py-2 text-left font-medium text-red-800">Field</th>
                                                    <th className="px-3 py-2 text-left font-medium text-red-800">Error</th>
                                                    <th className="px-3 py-2 text-left font-medium text-red-800">Value</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-red-200">
                                                {validationErrors.map((error, index) => (
                                                    <tr key={index} className="hover:bg-red-100">
                                                        <td className="px-3 py-2 font-medium text-red-900">{error.row}</td>
                                                        <td className="px-3 py-2 text-red-800">{error.field}</td>
                                                        <td className="px-3 py-2 text-red-700">{error.error}</td>
                                                        <td className="px-3 py-2 text-red-600 max-w-xs truncate" title={error.value}>{error.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            
                            {validationWarnings.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-center mb-3">
                                        <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <h4 className="font-semibold text-yellow-800">Warnings ({validationWarnings.length})</h4>
                                    </div>
                                    <div className="max-h-32 overflow-y-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-yellow-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-yellow-800">Row</th>
                                                    <th className="px-3 py-2 text-left font-medium text-yellow-800">Field</th>
                                                    <th className="px-3 py-2 text-left font-medium text-yellow-800">Warning</th>
                                                    <th className="px-3 py-2 text-left font-medium text-yellow-800">Value</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-yellow-200">
                                                {validationWarnings.map((warning, index) => (
                                                    <tr key={index} className="hover:bg-yellow-100">
                                                        <td className="px-3 py-2 font-medium text-yellow-900">{warning.row}</td>
                                                        <td className="px-3 py-2 text-yellow-800">{warning.field}</td>
                                                        <td className="px-3 py-2 text-yellow-700">{warning.warning}</td>
                                                        <td className="px-3 py-2 text-yellow-600 max-w-xs truncate" title={warning.value}>{warning.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {preview.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Preview (First 5 rows)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Highlights</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pros</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {preview.map((row, index) => (
                                            <tr key={index}>
                                                <td className="px-3 py-2 text-sm text-gray-900">{row.name || row.Name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600">{row.brand || row.Brand}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600">{row.categoryName || row['Category Name'] || row.category}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600">${row.price || row.Price || '0'}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate">
                                                    {(row.highlights || row.Highlights || '').split(';').length > 1 ? `${(row.highlights || row.Highlights || '').split(';').length} items` : (row.highlights || row.Highlights || 'None')}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate">
                                                    {(row.pros || row.Pros || '').split(';').length > 1 ? `${(row.pros || row.Pros || '').split(';').length} items` : (row.pros || row.Pros || 'None')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Upload Progress Indicator */}
                    {uploading && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                            {uploadProgress.total === 0 ? (
                                /* Initial loading state - reading CSV */
                                <div className="flex items-center justify-center py-4">
                                    <svg className="animate-spin h-6 w-6 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="font-semibold text-blue-900 text-lg">
                                        Reading CSV file...
                                    </span>
                                </div>
                            ) : (
                                /* Upload progress state */
                                <>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center">
                                            <svg className="animate-spin h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="font-semibold text-blue-900">
                                                {uploadProgress.status === 'parsing' ? 'Preparing categories...' : 'Uploading Products'}
                                            </span>
                                        </div>
                                        <span className="text-lg font-bold text-blue-700">
                                            {uploadProgress.current} / {uploadProgress.total}
                                        </span>
                                    </div>
                                    
                                    {/* Progress Bar */}
                                    <div className="w-full bg-blue-200 rounded-full h-4 mb-3 overflow-hidden">
                                        <div 
                                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-300 ease-out"
                                            style={{ 
                                                width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` 
                                            }}
                                        ></div>
                                    </div>
                                    
                                    {/* Current Product Name */}
                                    {uploadProgress.currentProductName && (
                                        <div className="text-sm text-blue-700 mb-2">
                                            <span className="font-medium">Processing:</span>{' '}
                                            <span className="truncate inline-block max-w-md align-bottom font-mono">
                                                {uploadProgress.currentProductName}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Percentage and Stats */}
                                    <div className="flex justify-between items-center text-sm text-blue-600 mt-2">
                                        <span>
                                            {uploadProgress.current} of {uploadProgress.total} products uploaded
                                        </span>
                                        <span className="font-bold">
                                            {uploadProgress.total > 0 
                                                ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
                                                : 0}% complete
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-4">
                        <button
                            onClick={handleClose}
                            disabled={uploading}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? 'Please wait...' : 'Cancel'}
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!file || uploading || validationErrors.length > 0}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading 
                                ? `Uploading... (${uploadProgress.current}/${uploadProgress.total})` 
                                : validationErrors.length > 0 
                                    ? 'Fix Errors First' 
                                    : 'Upload Products'
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductBulkUploadModal;
